import { MutationOptions, useMutation } from '@tanstack/react-query';
import axios from 'axios';
import fileDownload from 'js-file-download';
import apiConfig from '../../../constants/apiConfig';

export interface NotefolderItem {
  id: string;
  folder_name: string;
  color?: string;
  type: 'folder';
  notefolderId?: string | null;
  lastModified?: string | null;
}

export interface NotefileItem {
  id: string;
  file_name: string;
  fileUrl: string;
  size?: number;
  mimetype?: string;
  iconUrl?: string | null;
  type: 'file';
  notefolderId?: string | null;
  lastModified?: string | null;
}

type SignedUrlResponse = {
  signedUrl: string;
  expiresAt?: string;
};

export const fetchDownloadSignedUrl = async (blobUrl: string) => {
  const { data } = await axios.post<SignedUrlResponse>(
    apiConfig.apiGateway.fileDownloadUrl,
    { blobUrl }
  );
  return data.signedUrl;
};

export const useSignedUrl = ({
  onSuccess,
}: MutationOptions<string, unknown, string> = {}) => {
  return useMutation({
    mutationFn: async (blobUrl: string) =>
      await fetchDownloadSignedUrl(blobUrl),
    onSuccess,
  });
};

// Mints a signed URL for `blobUrl`, fetches the bytes, and saves them to disk.
// Pass `fileName` whenever the caller has the managed-file row in hand — the
// derived fallback only inspects the URL and can produce confusing names
// because COMS stores blobs as `<hostHash>/<owner>/<filenameHash>` with no
// preserved extension.
export const downloadBlobAsFile = async (
  blobUrl: string,
  fileName?: string
): Promise<void> => {
  const signedUrl = await fetchDownloadSignedUrl(blobUrl);
  const res = await fetch(signedUrl, { headers: {} });
  const blob = await res.blob();
  fileDownload(
    blob,
    fileName && fileName.trim() ? fileName : deriveDownloadName(blobUrl)
  );
};

// Best-effort filename derivation when no file_name is available. Decodes the
// last path segment; if it matches the historical `<owner>-<original>`
// convention (trailing dash-segment with an extension), returns that segment,
// otherwise returns the whole last segment.
function deriveDownloadName(blobUrl: string): string {
  let lastSegment = blobUrl.split('?')[0].split('/').pop() ?? '';
  try {
    lastSegment = decodeURIComponent(lastSegment);
  } catch {
    // ignore malformed percent-encoding
  }
  if (!lastSegment) return 'download';
  const trailing = lastSegment.split('-').pop() ?? lastSegment;
  return trailing.includes('.') ? trailing : lastSegment;
}
